<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'

const isMenuOpen = ref(false)

const computeServices = [
  { name: 'Dedicated CPU', path: '/compute/dedicated-cpu', description: 'High-performance dedicated CPU instances', icon: 'fas fa-microchip' },
  { name: 'Shared CPU', path: '/compute/shared-cpu', description: 'Cost-effective shared CPU resources', icon: 'fas fa-server' },
  { name: 'Dedicated GPU', path: '/compute/dedicated-gpu', description: 'Powerful GPU instances for ML and rendering', icon: 'fas fa-desktop' },
  { name: 'Bare Metal', path: '/compute/bare-metal', description: 'Raw hardware access for maximum performance', icon: 'fas fa-layer-group' },
  { name: 'Colocation', path: '/compute/colocation', description: 'Bring your own hardware to our facilities', icon: 'fas fa-building' }
]

const platformServices = [
  { name: 'Services', path: '/services', description: 'Explore our cloud services', icon: 'fas fa-cloud' },
  { name: 'Data Centers', path: '/data-centers', description: 'Global infrastructure network', icon: 'fas fa-globe' },
  { name: 'Security & Compliance', path: '/security', description: 'Our security measures and certifications', icon: 'fas fa-shield-alt' },
  { name: 'Documentation', path: '/docs', description: 'Platform and API documentation', icon: 'fas fa-book' },
  { name: 'Integrations', path: '/integrations', description: 'Connect your favorite tools and services', icon: 'fas fa-plug' }
]

const companyMenu = {
  'About Us': [
    { name: 'Our Story', path: '/about/story', icon: 'fas fa-book-open' },
    { name: 'Mission & Values', path: '/about/mission', icon: 'fas fa-bullseye' },
    { name: 'Careers', path: '/careers', icon: 'fas fa-briefcase' }
  ],
  'Media': [
    { name: 'Press Releases', path: '/media/press', icon: 'fas fa-newspaper' },
    { name: 'Media Kit', path: '/media/media-kit', icon: 'fas fa-camera' },
    { name: 'Awards', path: '/media/awards', icon: 'fas fa-trophy' }
  ],
  'Legal': [
    { name: 'Terms of Service', path: '/legal/terms', icon: 'fas fa-gavel' },
    { name: 'Privacy Policy', path: '/legal/privacy', icon: 'fas fa-user-shield' },
    { name: 'Acceptable Use', path: '/legal/acceptable-use', icon: 'fas fa-check-circle' }
  ],
  'Contact': [
    { name: 'General Inquiries', path: '/contact', icon: 'fas fa-envelope' }
  ]
}

const mainNavigation = [
  { name: 'Blog', path: '/blog' },
  { name: 'Status', path: '/status' }
]
</script>

<template>
  <nav class="fixed w-full z-50 top-0 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <div class="flex items-center">
          <RouterLink to="/" class="flex items-center group">
            <i class="fas fa-cloud text-2xl text-primary-400 mr-2 transition-transform group-hover:scale-110"></i>
            <span class="text-2xl font-bold bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">Syniac</span>
          </RouterLink>

          <div class="hidden md:ml-10 md:flex md:space-x-8">
            <!-- Compute Dropdown -->
            <Menu as="div" class="relative">
              <MenuButton class="text-gray-300 hover:text-primary-400 inline-flex items-center px-1 py-2 text-sm font-medium border-b-2 border-transparent hover:border-primary-400">
                <span>Compute</span>
                <i class="fas fa-chevron-down ml-2 text-xs"></i>
              </MenuButton>

              <MenuItems class="absolute left-0 mt-2 w-80 rounded-lg shadow-lg bg-gray-800 ring-1 ring-black ring-opacity-5 divide-y divide-gray-700">
                <div class="py-1">
                  <MenuItem v-for="service in computeServices" :key="service.name" v-slot="{ active }">
                    <RouterLink
                      :to="service.path"
                      :class="[
                        active ? 'bg-gray-700 text-primary-400' : 'text-gray-300',
                        'group flex items-center px-4 py-3 hover:bg-gray-700/50'
                      ]"
                    >
                      <i :class="[service.icon, 'w-6 text-primary-400']"></i>
                      <div class="ml-3">
                        <p class="font-medium">{{ service.name }}</p>
                        <p class="text-sm text-gray-400">{{ service.description }}</p>
                      </div>
                    </RouterLink>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>

            <!-- Platform Dropdown -->
            <Menu as="div" class="relative">
              <MenuButton class="text-gray-300 hover:text-primary-400 inline-flex items-center px-1 py-2 text-sm font-medium border-b-2 border-transparent hover:border-primary-400">
                <span>Platform</span>
                <i class="fas fa-chevron-down ml-2 text-xs"></i>
              </MenuButton>

              <MenuItems class="absolute left-0 mt-2 w-80 rounded-lg shadow-lg bg-gray-800 ring-1 ring-black ring-opacity-5 divide-y divide-gray-700">
                <div class="py-1">
                  <MenuItem v-for="service in platformServices" :key="service.name" v-slot="{ active }">
                    <RouterLink
                      :to="service.path"
                      :class="[
                        active ? 'bg-gray-700 text-primary-400' : 'text-gray-300',
                        'group flex items-center px-4 py-3 hover:bg-gray-700/50'
                      ]"
                    >
                      <i :class="[service.icon, 'w-6 text-primary-400']"></i>
                      <div class="ml-3">
                        <p class="font-medium">{{ service.name }}</p>
                        <p class="text-sm text-gray-400">{{ service.description }}</p>
                      </div>
                    </RouterLink>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>

            <!-- Company Dropdown -->
            <Menu as="div" class="relative">
              <MenuButton class="text-gray-300 hover:text-primary-400 inline-flex items-center px-1 py-2 text-sm font-medium border-b-2 border-transparent hover:border-primary-400">
                <span>Company</span>
                <i class="fas fa-chevron-down ml-2 text-xs"></i>
              </MenuButton>

              <MenuItems class="absolute left-0 mt-2 w-[400px] rounded-lg shadow-lg bg-gray-800 ring-1 ring-black ring-opacity-5">
                <div class="grid grid-cols-2 gap-4 p-6">
                  <div v-for="(items, category) in companyMenu" :key="category" class="space-y-4">
                    <h3 class="text-sm font-semibold text-primary-400 uppercase tracking-wider">{{ category }}</h3>
                    <ul class="space-y-2">
                      <MenuItem v-for="item in items" :key="item.name" v-slot="{ active }">
                        <RouterLink
                          :to="item.path"
                          :class="[
                            active ? 'bg-gray-700 text-primary-400' : 'text-gray-300',
                            'group flex items-center px-3 py-2 text-sm rounded-md hover:bg-gray-700/50'
                          ]"
                        >
                          <i :class="[item.icon, 'w-5 text-primary-400']"></i>
                          <span class="ml-3">{{ item.name }}</span>
                        </RouterLink>
                      </MenuItem>
                    </ul>
                  </div>
                </div>
              </MenuItems>
            </Menu>

            <RouterLink
              v-for="item in mainNavigation"
              :key="item.name"
              :to="item.path"
              class="text-gray-300 hover:text-primary-400 inline-flex items-center px-1 py-2 text-sm font-medium border-b-2 border-transparent hover:border-primary-400"
            >
              {{ item.name }}
            </RouterLink>
          </div>
        </div>

        <div class="hidden md:flex items-center space-x-4">
          <RouterLink
            to="/contact"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-400 border border-primary-400 rounded-md hover:bg-primary-400 hover:text-white"
          >
            Contact Sales
          </RouterLink>
          <a
            href="https://billing.syniac.com"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-md hover:bg-primary-600"
          >
            Login
          </a>
        </div>

        <!-- Mobile menu button -->
        <div class="md:hidden flex items-center">
          <button
            @click="isMenuOpen = !isMenuOpen"
            class="text-gray-300 hover:text-primary-400 p-2"
          >
            <i :class="[isMenuOpen ? 'fas fa-times' : 'fas fa-bars']" class="text-xl"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <div
      v-show="isMenuOpen"
      class="md:hidden bg-gray-900 border-b border-gray-800"
    >
      <div class="px-2 pt-2 pb-3 space-y-1">
        <!-- Mobile Compute Menu -->
        <div class="px-3 py-2">
          <div class="text-gray-300 font-medium mb-2">Compute Services</div>
          <div class="space-y-2 pl-4">
            <RouterLink
              v-for="service in computeServices"
              :key="service.name"
              :to="service.path"
              class="flex items-center text-gray-400 hover:text-primary-400 py-2"
              @click="isMenuOpen = false"
            >
              <i :class="[service.icon, 'w-6']"></i>
              <span class="ml-2">{{ service.name }}</span>
            </RouterLink>
          </div>
        </div>

        <!-- Mobile Platform Menu -->
        <div class="px-3 py-2">
          <div class="text-gray-300 font-medium mb-2">Platform</div>
          <div class="space-y-2 pl-4">
            <RouterLink
              v-for="service in platformServices"
              :key="service.name"
              :to="service.path"
              class="flex items-center text-gray-400 hover:text-primary-400 py-2"
              @click="isMenuOpen = false"
            >
              <i :class="[service.icon, 'w-6']"></i>
              <span class="ml-2">{{ service.name }}</span>
            </RouterLink>
          </div>
        </div>

        <!-- Mobile Company Menu -->
        <div class="px-3 py-2">
          <div v-for="(items, category) in companyMenu" :key="category" class="mb-4">
            <div class="text-gray-300 font-medium mb-2">{{ category }}</div>
            <div class="space-y-2 pl-4">
              <RouterLink
                v-for="item in items"
                :key="item.name"
                :to="item.path"
                class="flex items-center text-gray-400 hover:text-primary-400 py-2"
                @click="isMenuOpen = false"
              >
                <i :class="[item.icon, 'w-6']"></i>
                <span class="ml-2">{{ item.name }}</span>
              </RouterLink>
            </div>
          </div>
        </div>

        <RouterLink
          v-for="item in mainNavigation"
          :key="item.name"
          :to="item.path"
          class="block px-3 py-2 text-base font-medium text-gray-300 hover:text-primary-400"
          @click="isMenuOpen = false"
        >
          {{ item.name }}
        </RouterLink>

        <div class="px-3 pt-4 space-y-2">
          <RouterLink
            to="/contact"
            class="block w-full px-4 py-2 text-center text-primary-400 border border-primary-400 rounded-md hover:bg-primary-400 hover:text-white"
            @click="isMenuOpen = false"
          >
            Contact Sales
          </RouterLink>
          <a
            href="https://billing.syniac.com"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-md hover:bg-primary-600"
          >
            Login
          </a>
        </div>
      </div>
    </div>
  </nav>
</template>
